import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Settings as SettingsIcon, UserPlus, ShieldCheck, Wrench, Users as UsersIcon,
  Save, Pencil, Trash2, KeyRound, Mail, CheckCircle2, XCircle,
} from "lucide-react";

const ROLES = [
  { value: "admin", label: "Admin", description: "Acesso total ao sistema", icon: ShieldCheck },
  { value: "mechanic", label: "Mechanic / Técnico", description: "Check-in, Kanban, Inspeção, Diagnóstico e Fila", icon: Wrench },
];

const ROLE_META = Object.fromEntries(ROLES.map((r) => [r.value, r]));

export default function Configuracoes() {
  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="configuracoes-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
          <SettingsIcon className="w-3.5 h-3.5" /> Configurações
        </div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">Painel Administrativo</h1>
      </div>
      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users" data-testid="tab-users">
            <UsersIcon className="w-3.5 h-3.5 mr-1.5" /> Usuários & Permissões
          </TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-6">
          <UsersManagement />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function UsersManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar usuários");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.user_id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Usuário desativado" : "Usuário ativado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao alterar status");
    }
  };

  const del = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/users/${confirmDel.user_id}`);
      toast.success("Usuário removido");
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao remover");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold">Usuários do sistema</h2>
          <p className="text-sm text-muted-foreground">Gerencie quem tem acesso e o nível de permissão.</p>
        </div>
        <Button onClick={() => setOpenCreate(true)} data-testid="new-user-btn">
          <UserPlus className="w-4 h-4 mr-1.5" /> Novo Usuário
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-12 text-center">Carregando…</div>
      ) : users.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-12 text-center">
          <UsersIcon className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <div className="text-sm font-medium">Nenhum usuário cadastrado além do Admin master.</div>
          <div className="text-xs text-muted-foreground mt-1">Crie um usuário para dar acesso limitado à equipe.</div>
        </div>
      ) : (
        <div className="border border-border rounded-md overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 bg-secondary/60 text-[10px] uppercase tracking-widest text-muted-foreground">
            <div className="col-span-3">Funcionário</div>
            <div className="col-span-2">Usuário</div>
            <div className="col-span-3">E-mail</div>
            <div className="col-span-1">Perfil</div>
            <div className="col-span-1">Ativo</div>
            <div className="col-span-2 text-right">Ações</div>
          </div>
          {users.map((u) => {
            const meta = ROLE_META[u.role] || ROLE_META.mechanic;
            const Icon = meta.icon;
            const isSelf = u.user_id === me?.user_id;
            return (
              <div
                key={u.user_id}
                className="md:grid md:grid-cols-12 md:gap-3 flex flex-col md:items-center px-4 py-3 border-t border-border gap-2"
                data-testid={`user-row-${u.user_id}`}
              >
                <div className="md:col-span-3 flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">
                      {u.employee_name || u.name} {isSelf && <span className="text-[10px] uppercase tracking-widest text-primary ml-1">Você</span>}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate md:hidden">@{u.username} · {u.email || "—"}</div>
                  </div>
                </div>
                <div className="hidden md:block col-span-2 text-sm font-mono text-foreground truncate">
                  @{u.username || "—"}
                </div>
                <div className="hidden md:block col-span-3 text-sm text-muted-foreground truncate">
                  <Mail className="w-3 h-3 inline mr-1" />{u.email || "—"}
                </div>
                <div className="md:col-span-1 text-xs">
                  <span className={`px-2 py-0.5 rounded border text-[10px] uppercase tracking-widest font-bold ${
                    u.role === "admin" ? "border-primary/40 text-primary bg-primary/5" : "border-border text-muted-foreground bg-secondary"
                  }`}>
                    {meta.label.split(" ")[0]}
                  </span>
                </div>
                <div className="md:col-span-1 flex items-center gap-1.5">
                  <Switch
                    checked={u.is_active}
                    onCheckedChange={() => toggleActive(u)}
                    disabled={isSelf}
                    data-testid={`toggle-active-${u.user_id}`}
                  />
                  {u.is_active
                    ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 hidden md:block" />
                    : <XCircle className="w-3.5 h-3.5 text-muted-foreground hidden md:block" />}
                </div>
                <div className="md:col-span-2 flex md:justify-end gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditUser(u)} data-testid={`edit-user-${u.user_id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => setConfirmDel(u)}
                    disabled={isSelf}
                    className="text-muted-foreground hover:text-red-500"
                    data-testid={`delete-user-${u.user_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CreateUserDialog open={openCreate} onOpenChange={setOpenCreate} onSaved={load} />
      <EditUserDialog user={editUser} onOpenChange={(v) => !v && setEditUser(null)} onSaved={load} />
      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle>Remover usuário</DialogTitle>
            <DialogDescription>
              O usuário <b>{confirmDel?.employee_name || confirmDel?.name}</b> (<span className="font-mono">@{confirmDel?.username}</span>) será excluído permanentemente e suas sessões revogadas.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={del} data-testid="delete-user-confirm">
              <Trash2 className="w-4 h-4 mr-1.5" /> Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateUserDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ employee_name: "", username: "", email: "", password: "", role: "mechanic" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ employee_name: "", username: "", email: "", password: "", role: "mechanic" });
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_name.trim() || !form.username.trim() || !form.password) {
      return toast.error("Preencha nome do funcionário, usuário e senha");
    }
    if (!/^[a-zA-Z0-9._-]{3,32}$/.test(form.username.trim())) {
      return toast.error("Usuário deve ter 3-32 caracteres (letras, números, . _ -)");
    }
    if (form.password.length < 6) return toast.error("Senha deve ter ao menos 6 caracteres");
    setSaving(true);
    try {
      await api.post("/users", {
        employee_name: form.employee_name.trim(),
        username: form.username.trim().toLowerCase(),
        email: form.email.trim() || undefined,
        password: form.password,
        role: form.role,
      });
      toast.success("Usuário criado");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao criar usuário");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle>Novo usuário</DialogTitle>
          <DialogDescription>Este usuário poderá acessar o sistema com o perfil selecionado. O login é feito pelo campo &quot;Usuário&quot;.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Nome do funcionário</Label>
            <Input
              value={form.employee_name}
              onChange={(e) => setForm({ ...form, employee_name: e.target.value })}
              autoFocus
              placeholder="Ex: João da Silva"
              data-testid="new-user-employee-name"
            />
          </div>
          <div>
            <Label>Usuário (login)</Label>
            <Input
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })}
              placeholder="ex: joao.silva"
              autoComplete="off"
              spellCheck={false}
              data-testid="new-user-username"
            />
            <div className="text-[10px] text-muted-foreground mt-1">3-32 caracteres: letras minúsculas, números, ponto, hífen ou underline.</div>
          </div>
          <div>
            <Label>E-mail (opcional — apenas contato)</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" />
          </div>
          <div>
            <Label>Senha (mínimo 6 caracteres)</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" />
          </div>
          <div>
            <Label>Perfil de acesso</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    <div>
                      <div className="font-medium">{r.label}</div>
                      <div className="text-[11px] text-muted-foreground">{r.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="new-user-submit">
              <UserPlus className="w-4 h-4 mr-1.5" /> {saving ? "Criando…" : "Criar Usuário"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    if (user) setForm({
      employee_name: user.employee_name || user.name || "",
      username: user.username || "",
      email: user.email || "",
      role: user.role,
      is_active: user.is_active,
      password: "",
    });
    else setForm(null);
    setChangingPw(false);
  }, [user]);

  if (!user || !form) return null;

  const isMaster = (form.username || "").toLowerCase() === "admin";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_name.trim() || !form.username.trim()) return toast.error("Nome e usuário obrigatórios");
    if (!/^[a-zA-Z0-9._-]{3,32}$/.test(form.username.trim())) {
      return toast.error("Usuário deve ter 3-32 caracteres (letras, números, . _ -)");
    }
    if (changingPw && form.password && form.password.length < 6) return toast.error("Senha deve ter 6+ caracteres");
    setSaving(true);
    try {
      const payload = {
        employee_name: form.employee_name,
        username: form.username.trim().toLowerCase(),
        email: form.email || null,
        role: form.role,
        is_active: form.is_active,
      };
      if (changingPw && form.password) payload.password = form.password;
      await api.put(`/users/${user.user_id}`, payload);
      toast.success("Usuário atualizado");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle>Editar usuário</DialogTitle>
          <DialogDescription>Alterações são aplicadas imediatamente. Se trocar a senha, o usuário será desconectado.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Nome do funcionário</Label>
            <Input value={form.employee_name} onChange={(e) => setForm({ ...form, employee_name: e.target.value })} data-testid="edit-user-employee-name" />
          </div>
          <div>
            <Label>Usuário (login)</Label>
            <Input
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })}
              disabled={isMaster}
              data-testid="edit-user-username"
            />
            {isMaster && <div className="text-[10px] text-muted-foreground mt-1">Usuário master não pode ser renomeado.</div>}
          </div>
          <div>
            <Label>E-mail (contato)</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-user-email" />
          </div>
          <div>
            <Label>Perfil</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="edit-user-active" />
            <Label className="cursor-pointer" onClick={() => setForm({ ...form, is_active: !form.is_active })}>
              Usuário ativo
            </Label>
          </div>
          <div className="border-t border-border pt-3">
            {!changingPw ? (
              <Button type="button" variant="outline" onClick={() => setChangingPw(true)} data-testid="edit-user-change-pw">
                <KeyRound className="w-4 h-4 mr-1.5" /> Trocar senha
              </Button>
            ) : (
              <div>
                <Label>Nova senha (mínimo 6)</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoFocus data-testid="edit-user-password" />
                <Button type="button" variant="ghost" size="sm" onClick={() => { setChangingPw(false); setForm({ ...form, password: "" }); }} className="mt-1">
                  Cancelar troca de senha
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="edit-user-save">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Salvando…" : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
